import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Card, CardContent, Typography, Chip, TablePagination } from '@mui/material';
import { ResponsiveTable, Column } from '../../components/mobile/ResponsiveTable';
import { TableToolbar, FilterOption, ColumnConfig } from '../../components/TableToolbar';
import axiosInstance from '../../lib/axios';
import { palette } from '../../theme/colors';
import {
  CashDrawerShift,
  CashDrawerShiftStatus,
  PaginatedShiftsResponse,
} from '../../types/cashDrawer';

interface FetchShiftsResult {
  data: CashDrawerShift[];
  total: number;
}

const fetchShifts = async (
  page: number,
  pageSize: number,
  status?: string
): Promise<FetchShiftsResult> => {
  const params: Record<string, any> = {
    page: page + 1,
    limit: pageSize,
  };

  if (status) {
    params.status = status;
  }

  const response = await axiosInstance.get<PaginatedShiftsResponse>('/cash-drawer/shifts', {
    params,
  });

  return {
    data: response.data.data,
    total: response.data.pagination.total,
  };
};

const formatCurrency = (amount: number | null) => {
  if (amount === null || amount === undefined) return '—';
  return new Intl.NumberFormat('fr-DZ', {
    style: 'currency',
    currency: 'DZD',
    minimumFractionDigits: 0,
  }).format(amount);
};

const formatDateTime = (value: string | null) => {
  if (!value) return '—';
  return new Date(value).toLocaleString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getUserLabel = (user?: { firstName?: string; lastName?: string; email?: string } | null) => {
  if (!user) return '—';
  const name = `${user.firstName || ''} ${user.lastName || ''}`.trim();
  return name || user.email || '—';
};

interface CashDrawerListProps {
  onShiftClick?: (shift: CashDrawerShift) => void;
}

export const CashDrawerList = ({ onShiftClick }: CashDrawerListProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [activeFilters, setActiveFilters] = useState<Record<string, string>>({});

  const { data, isLoading } = useQuery({
    queryKey: ['cash-drawer-shifts', page, pageSize, activeFilters.status],
    queryFn: () => fetchShifts(page, pageSize, activeFilters.status),
  });

  const filteredData = useMemo(() => data?.data || [], [data?.data]);

  const filterOptions: FilterOption[] = [
    {
      field: 'status',
      label: t('cashDrawer.status.label'),
      type: 'select',
      options: [
        { value: CashDrawerShiftStatus.OPEN, label: t('cashDrawer.status.open') },
        { value: CashDrawerShiftStatus.CLOSED, label: t('cashDrawer.status.closed') },
      ],
    },
  ];

  const getExportColumns = (): ColumnConfig[] => [
    { field: 'openedAt', headerName: t('cashDrawer.list.openedAt'), valueFormatter: v => formatDateTime(v) },
    { field: 'closedAt', headerName: t('cashDrawer.list.closedAt'), valueFormatter: v => formatDateTime(v) },
    { field: 'openingBalance', headerName: t('cashDrawer.openingBalance'), valueFormatter: v => formatCurrency(v) },
    { field: 'expectedClosingBalance', headerName: t('cashDrawer.expectedClosingBalance'), valueFormatter: v => formatCurrency(v) },
    { field: 'countedClosingBalance', headerName: t('cashDrawer.countedClosingBalance'), valueFormatter: v => formatCurrency(v) },
    { field: 'discrepancy', headerName: t('cashDrawer.discrepancy'), valueFormatter: v => formatCurrency(v) },
    { field: 'status', headerName: t('cashDrawer.status.label') },
  ];

  const handleFilterChange = (field: string, value: string) => {
    setActiveFilters(prev => ({ ...prev, [field]: value }));
    setPage(0);
  };

  const handleClearFilters = () => {
    setActiveFilters({});
    setPage(0);
  };

  const handleRowClick = (shift: CashDrawerShift) => {
    if (onShiftClick) {
      onShiftClick(shift);
    } else {
      navigate(`/app/cash-drawer/${shift.id}`);
    }
  };

  const tableColumns: Column<CashDrawerShift>[] = [
    {
      field: 'openedAt',
      headerName: t('cashDrawer.list.openedAt'),
      render: value => <Typography variant="body2">{formatDateTime(value)}</Typography>,
    },
    {
      field: 'closedAt',
      headerName: t('cashDrawer.list.closedAt'),
      render: value => <Typography variant="body2">{formatDateTime(value)}</Typography>,
    },
    {
      field: 'openingBalance',
      headerName: t('cashDrawer.openingBalance'),
      render: value => <Typography variant="body2">{formatCurrency(value)}</Typography>,
    },
    {
      field: 'expectedClosingBalance',
      headerName: t('cashDrawer.expectedClosingBalance'),
      render: value => <Typography variant="body2">{formatCurrency(value)}</Typography>,
    },
    {
      field: 'countedClosingBalance',
      headerName: t('cashDrawer.countedClosingBalance'),
      render: value => <Typography variant="body2">{formatCurrency(value)}</Typography>,
    },
    {
      field: 'discrepancy',
      headerName: t('cashDrawer.discrepancy'),
      render: value => {
        if (value === null || value === undefined) {
          return <Typography variant="body2">—</Typography>;
        }
        const isPositive = value > 0;
        const isZero = value === 0;
        return (
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{ color: isZero ? undefined : isPositive ? palette.success : palette.error }}
          >
            {isPositive ? '+' : ''}
            {formatCurrency(value)}
          </Typography>
        );
      },
    },
    {
      field: 'status',
      headerName: t('cashDrawer.status.label'),
      render: value => (
        <Chip
          label={
            value === CashDrawerShiftStatus.OPEN
              ? t('cashDrawer.status.open')
              : t('cashDrawer.status.closed')
          }
          size="small"
          color={value === CashDrawerShiftStatus.OPEN ? 'success' : 'default'}
          variant={value === CashDrawerShiftStatus.OPEN ? 'filled' : 'outlined'}
        />
      ),
    },
    {
      field: 'openedByUser',
      headerName: t('cashDrawer.list.openedBy'),
      render: (value, row) => <Typography variant="body2">{getUserLabel(row.openedByUser)}</Typography>,
    },
    {
      field: 'closedByUser',
      headerName: t('cashDrawer.list.closedBy'),
      render: (value, row) => <Typography variant="body2">{getUserLabel(row.closedByUser)}</Typography>,
    },
  ];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4">{t('cashDrawer.list.title')}</Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            {t('cashDrawer.list.subtitle')}
          </Typography>
        </Box>
      </Box>

      <Card>
        <CardContent>
          <TableToolbar
            title={t('cashDrawer.list.title')}
            searchValue=""
            onSearchChange={() => {}}
            filters={filterOptions}
            activeFilters={activeFilters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            data={filteredData}
            columns={getExportColumns()}
            exportFileName="cash-drawer-shifts"
          />

          <ResponsiveTable
            columns={tableColumns}
            rows={filteredData}
            loading={isLoading}
            emptyMessage={t('cashDrawer.list.empty')}
            primaryField="openedAt"
            getPrimaryText={row => formatDateTime(row.openedAt)}
            getSecondaryText={row => getUserLabel(row.openedByUser)}
            onRowClick={handleRowClick}
          />
          <TablePagination
            component="div"
            count={data?.total || 0}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={e => {
              setPageSize(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default CashDrawerList;
